'use client';

import { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import RosterUploader from '@/components/RosterUploader';
import NameSelector from '@/components/NameSelector';
import ShiftTable from '@/components/ShiftTable';
import ShiftTimeConfig from '@/components/ShiftTimeConfig';
import IcsExporter from '@/components/IcsExporter';
import { ParsedRoster, Shift, ShiftTimeMapping } from '@/lib/types';
import { defaultCaseyMappings, defaultVhhMappings } from '@/lib/shift-mappings';

type Step = 'upload' | 'select-name' | 'review' | 'export';

const STEP_LABELS: Record<Step, string> = {
  'upload': 'Upload',
  'select-name': 'Select Name',
  'review': 'Review',
  'export': 'Export',
};

export default function Home() {
  const [step, setStep] = useState<Step>('upload');
  const [roster, setRoster] = useState<ParsedRoster | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftsVersion, setShiftsVersion] = useState(0);
  const [customMappings, setCustomMappings] = useState<ShiftTimeMapping[]>([]);

  const [feedbackSummary, setFeedbackSummary] = useState('');
  const [feedbackBody, setFeedbackBody] = useState('');
  const [feedbackSteps, setFeedbackSteps] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileAvailable = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (turnstileAvailable && !turnstileToken) return;
    setFeedbackSubmitting(true);
    setFeedbackError('');
    setFeedbackDone(false);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: feedbackSummary,
          description: feedbackBody,
          steps: feedbackSteps,
          format: roster?.format === 'mmc' ? 'MMC (Monash Medical Centre)' : roster?.format === 'vhh' ? 'VHH (Valley Hospital)' : roster?.format === 'casey' ? 'Casey Hospital' : 'Unknown',
          turnstileToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create issue');
      setFeedbackDone(true);
      setFeedbackSummary('');
      setFeedbackBody('');
      setFeedbackSteps('');
      setTurnstileToken('');
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleShiftsChange = (updated: Shift[]) => {
    setShifts(updated);
    setShiftsVersion(v => v + 1);
  };

  const handleParsed = (parsed: ParsedRoster) => {
    setRoster(parsed);
    setCustomMappings([]);
    setStep('select-name');
  };

  const handleNameSelect = (name: string) => {
    setSelectedName(name);
    if (roster) {
      const raw = roster.shiftsByName.get(name) || [];
      setShifts([...raw].sort((a, b) => a.date.getTime() - b.date.getTime()));
    }
    setStep('review');
  };

  const unknownCodes = roster?.format === 'casey' || roster?.format === 'vhh'
    ? (() => {
        const defaults = roster?.format === 'vhh' ? defaultVhhMappings : defaultCaseyMappings;
        return shifts
          .filter(s => s.type === 'other' || (s.type === 'shift' && !s.startTime))
          .map(s => s.label)
          .filter((v, i, a) => a.indexOf(v) === i)
          .filter(code => !defaults.some(m => m.code === code))
          .filter(code => !customMappings.some(m => m.code === code));
      })()
    : [];

  const handleReset = () => {
    setStep('upload');
    setRoster(null);
    setSelectedName('');
    setShifts([]);
    setCustomMappings([]);
  };

  const stepIndex = (s: Step) => (['upload', 'select-name', 'review', 'export'] as Step[]).indexOf(s);

  return (
    <div className="flex flex-col flex-1 items-center justify-center bg-zinc-50 dark:bg-black font-sans">
      <main className="flex flex-col flex-1 w-full max-w-3xl items-center py-16 px-6">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-2">
          Roster Convert
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400 mb-1 text-center">
          Upload your roster, find your shifts, tweak and export to your calendar.
        </p>
        <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-8 text-center">
          No data is stored on this server. No warranty provided on accuracy — use at your own risk.
        </p>

        <div className="flex items-center gap-2 mb-8 text-sm">
          {(Object.keys(STEP_LABELS) as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={i <= stepIndex(step) ? 'font-medium text-zinc-900 dark:text-zinc-100' : 'text-zinc-400'}>
                {STEP_LABELS[s]}
              </span>
              {i < 3 && <span className="text-zinc-300">→</span>}
            </div>
          ))}
        </div>

        <div className="w-full flex flex-col items-center gap-6">
          {step === 'upload' && <RosterUploader onParsed={handleParsed} />}

          {step === 'select-name' && roster && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Found {roster.names.length} staff — {roster.format === 'mmc' ? 'MMC' : roster.format === 'vhh' ? 'VHH' : 'Casey'} format.
              </p>
              <NameSelector names={roster.names} onSelect={handleNameSelect} />
              <button
                onClick={handleReset}
                className="text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
              >
                ← Upload different file
              </button>
            </>
          )}

          {step === 'review' && roster && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Shifts for <strong>{selectedName}</strong>
              </p>
              {shifts.length === 0 ? (
                <p className="text-zinc-500">No shifts found.</p>
              ) : (
                <ShiftTable shifts={shifts} onShiftsChange={handleShiftsChange} format={roster.format as 'mmc' | 'casey' | 'vhh'} />
              )}
              {unknownCodes.length > 0 && (
                <ShiftTimeConfig
                  unknownCodes={unknownCodes}
                  customMappings={customMappings}
                  onUpdateMappings={setCustomMappings}
                />
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select-name')}
                  className="px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-md text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  ← Pick different name
                </button>
                <button
                  onClick={() => setStep('export')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700"
                >
                  Continue to export →
                </button>
              </div>
            </>
          )}

          {step === 'export' && roster && (
            <>
              <IcsExporter
                key={shiftsVersion}
                shifts={shifts}
                userName={selectedName}
                format={roster.format as 'mmc' | 'casey' | 'vhh'}
                customMappings={customMappings}
              />
              <button
                onClick={() => setStep('review')}
                className="text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
              >
                ← Back to review
              </button>
            </>
          )}
        </div>

        <details className="mt-12 w-full max-w-md text-sm group">
          <summary className="cursor-pointer text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-400 transition-colors select-none text-xs">
            Feedback? Something not right?
          </summary>
          <form onSubmit={handleFeedbackSubmit} className="mt-3 flex flex-col gap-3">
            <input
              type="text"
              placeholder="Summary"
              value={feedbackSummary}
              onChange={e => setFeedbackSummary(e.target.value)}
              className="w-full border border-zinc-300 dark:border-zinc-700 rounded px-3 py-1.5 text-xs bg-white dark:bg-black text-zinc-800 dark:text-zinc-200 focus:outline-none focus:border-zinc-500"
              required
            />
            <textarea
              placeholder="Describe the issue…"
              value={feedbackBody}
              onChange={e => setFeedbackBody(e.target.value)}
              rows={3}
              className="w-full border border-zinc-300 dark:border-zinc-700 rounded px-3 py-1.5 text-xs bg-white dark:bg-black text-zinc-800 dark:text-zinc-200 focus:outline-none focus:border-zinc-500 resize-none"
              required
            />
            <textarea
              placeholder="Steps to reproduce (optional)"
              value={feedbackSteps}
              onChange={e => setFeedbackSteps(e.target.value)}
              rows={2}
              className="w-full border border-zinc-300 dark:border-zinc-700 rounded px-3 py-1.5 text-xs bg-white dark:bg-black text-zinc-800 dark:text-zinc-200 focus:outline-none focus:border-zinc-500 resize-none"
            />
            <div className="flex justify-center [&_iframe]:!bg-transparent">
              {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
                <Turnstile
                  siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  options={{ theme: 'dark' }}
                />
              )}
            </div>
            <button
              type="submit"
              disabled={feedbackSubmitting || (turnstileAvailable && !turnstileToken)}
              className="self-center px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-400 text-white rounded text-xs font-medium transition-colors"
            >
              {feedbackSubmitting ? 'Sending…' : 'Open'}
            </button>
            {feedbackError && <p className="text-xs text-red-500">{feedbackError}</p>}
            {feedbackDone && <p className="text-xs text-emerald-600 dark:text-emerald-400">Issue created on GitHub.</p>}
          </form>
        </details>
      </main>
    </div>
  );
}